import type { CancelEventParam, CreateEventParam, Meter, ShopMeta, UpdatePageParam } from './types';
import { mutations as TYPES } from './types';
import { State } from './state';
import store from '@/app/store/store';
import type { IShopInfoV7, TypeShop } from '@/app/types/editor/shop';
import type { TypePage } from '@/app/types/editor/page';
import { PLAN_NAMES } from '@modules/common';

const setPlanPermissions = (
  contentScheduling: boolean,
  lazyLoad: boolean,
  seasonalAndHolidayTemplate: boolean,
  shareLibrary: boolean,
  layoutHeaderFooter: boolean,
  popupElement: boolean,
  stickyBlock: boolean,
  saveToLibrary: boolean,
  exportFromLibrary: boolean,
) => {
  return {
    contentScheduling,
    lazyLoad,
    seasonalAndHolidayTemplate,
    shareLibrary,
    layoutHeaderFooter,
    popupElement,
    stickyBlock,
    saveToLibrary,
    exportFromLibrary,
  };
};

const mutations = {
  /**
   * Reset
   * @param state State
   */
  [TYPES.RESET](state: State) {
    state = new State();
  },

  [TYPES.MUTATE_PAGE_INFO](state: State, value: TypePage) {
    state.pageInfo = value;
  },
  [TYPES.SET_USE_TO_DRAGGABLE](state: State, value: boolean) {
    state.usedToDragDrop = value;
  },
  [TYPES.UPDATE_LATEST_PRODUCT_IN_SHOPIFY](state: State, value: any) {
    state.latestProductInShopifyStore = value;
  },
  [TYPES.MUTATE_SHOP_INFO](state: State, value: TypeShop) {
    state.shopInfo = value;
    store.setValue('gemSession', value?.gemSession);
    let planPermissions = {
      contentScheduling: false,
      lazyLoad: false,
      seasonalAndHolidayTemplate: false,
      shareLibrary: false,
      layoutHeaderFooter: false,
      popupElement: false,
      stickyBlock: false,
      saveToLibrary: false,
      exportFromLibrary: false,
    };
    switch (value?.plan) {
      case PLAN_NAMES.TRIAL:
        planPermissions = setPlanPermissions(true, false, true, false, true, false, true, true, true);
        break;
      case PLAN_NAMES.STARTER:
        planPermissions = setPlanPermissions(false, false, false, false, false, true, true, true, false);
        break;
      case PLAN_NAMES.PROFESSIONAL:
        planPermissions = setPlanPermissions(false, true, true, true, true, true, true, true, true);
        break;
      case PLAN_NAMES.ADVANCE:
        planPermissions = setPlanPermissions(true, true, true, true, true, true, true, true, true);
        break;
      case PLAN_NAMES.BUILD:
        planPermissions = setPlanPermissions(false, true, false, true, true, false, true, true, true);
        break;
      case PLAN_NAMES.OPTIMIZE:
        planPermissions = setPlanPermissions(true, true, true, true, true, true, true, true, true);
        break;
      case PLAN_NAMES.ENTERPRISE:
        planPermissions = setPlanPermissions(true, true, true, true, true, true, true, true, true);
        break;
      default:
        break;
    }

    // check for avaible
    if (value && value.planLimitation) {
      const planLimitation = value.planLimitation[value?.plan];
      if (planLimitation) {
        const { conversionOptimization, elements, landingPage, pageOptimization, workflow, keyFeatures } =
          planLimitation;
        const { page_schedule, pageTransfer } = workflow;
        const { lazyLoading } = pageOptimization;
        const { seasonalAndHolidayTemplate, hiddenHeaderFooter } = landingPage;
        const { popupElement } = elements;
        const { stickyAddToCart } = conversionOptimization;
        const { insertBlockTemplate, exportLibrary } = keyFeatures;
        planPermissions = setPlanPermissions(
          page_schedule?.enabled,
          lazyLoading?.enabled,
          seasonalAndHolidayTemplate?.enabled,
          pageTransfer?.enabled,
          hiddenHeaderFooter?.enabled,
          popupElement?.enabled,
          stickyAddToCart?.enabled,
          exportLibrary?.enabled,
          insertBlockTemplate?.enabled,
        );
      }
    }
    state.planPermissions = planPermissions;
  },
  [TYPES.UPDATE_USER_SCOPE](state: State, value: string) {
    if (state.shopInfo?.userScope !== undefined) {
      state.shopInfo.userScope = value;
    }
  },
  [TYPES.MUTATE_STATE_SAVED](state: State, value: boolean) {
    state.isSaved = value;
  },
  [TYPES.MUTATE_STATE_MAKE_DEFAULT](state: State, value: boolean) {
    state.isMakeDefault = value;
  },
  [TYPES.UPDATE_PAGE_INFO](state: State, data: UpdatePageParam) {
    if (state.pageInfo?.page) {
      // Do nothing
    }
  },
  [TYPES.CREATE_PAGE_EVENT](state: State, data: CreateEventParam) {
    if (state.pageInfo?.page) {
      const page = state.pageInfo.page;
      if (!page.events) {
        page.events = [];
      }
      const eventData: any = {};
      if (data.fields) {
        for (const key in data.fields) {
          eventData[data.fields[key]] = data.body[key];
        }
      }
      const index = page.events.findIndex((x: any) => x.event == data.event);
      if (index > -1) {
        const item = {
          ...page.events[index],
          ...eventData,
        };
        page.events.splice(index, 1, item);
      } else {
        page.events.push({
          event: data.event,
          ...eventData,
        });
      }
    }
    store.setValue('__page', state.pageInfo);
  },
  [TYPES.CANCEL_PAGE_EVENT](state: State, data: CancelEventParam) {
    if (state.pageInfo?.page) {
      const page = state.pageInfo.page;
      if (!page.events) {
        page.events = [];
      }
      const index = page.events.findIndex((x: any) => x.event == data.event);
      if (index > -1) {
        page.events.splice(index, 1);
      } else {
        // Do nothing because event is not in the array
      }
    }
    store.setValue('__page', state.pageInfo);
  },
  [TYPES.UPDATE_PAGE_EVENTS](state: State, data: any) {
    if (state.pageInfo?.page) {
      const page = state.pageInfo.page;
      page.events = data;
    }
    store.setValue('__page', state.pageInfo);
  },
  [TYPES.UPDATE_PAGE_STATUS_APPLIED_GS](state: State, status: any) {
    if (state.pageInfo?.page) {
      const page = state.pageInfo.page;
      page.contentPageAppliedGs = status;
    }
    store.setValue('__page', state.pageInfo);
  },
  [TYPES.UPDATE_PAGE_STATUS_PUBLISHED_GS](state: State, status: any) {
    if (state.pageInfo?.page) {
      const page = state.pageInfo.page;
      page.publishedWithGs = status;
    }
    store.setValue('__page', state.pageInfo);
  },
  [TYPES.UPDATE_PAGE_PRESET_ID](state: State, presetId: number) {
    if (state.pageInfo?.page) {
      const page = state.pageInfo.page;
      // eslint-disable-next-line camelcase
      // eslint-disable-next-line @typescript-eslint/camelcase
      page.preset_id = presetId;
    }
    store.setValue('__page', state.pageInfo);
  },
  [TYPES.UPDATE_PAGE_IS_PUBLISHED](state: State, status: any) {
    if (state.pageInfo?.page) {
      const page = state.pageInfo.page;
      page.isPublished = status;
    }
    store.setValue('__page', state.pageInfo);
  },
  CANCEL_GET_SOURCE_MODAL(state: State, isCanceling: boolean) {
    if (isCanceling) {
      state.showGetSourceModal = false;
      state.getSourceModalType = '';
    }
  },
  OPEN_GET_SOURCE_MODAL(state: State, type = '') {
    if (type.length) {
      state.showGetSourceModal = true;
      state.getSourceModalType = type;
    }
  },
  DISABLE_HISTORY_ITEM(state: State, isDisable = true) {
    state.disabledHistoryItem = isDisable;
  },
  SHOW_PLAN_MODAL(state: State, isShow = false) {
    state.showPlanPopup = isShow;
  },
  SHOW_PAGE_SETTING_MODAL(state: State, isShow = false) {
    state.showPageSettingModal = isShow;
  },
  [TYPES.SHOP_META](state: State, shopMeta: ShopMeta) {
    if (!shopMeta) return;
    state.shopMeta = shopMeta;
  },
  [TYPES.SET_FONT_META](state: State, fontMeta: any) {
    if (!fontMeta) return;
    const fontState = state.fontMeta;
    const key = fontMeta.key;
    if (key === 'active-font' && fontState.activeFonts == null) {
      fontState.activeFonts = fontMeta.value;
    } else if (key === 'theme-font' && fontState.themeFonts == null) {
      fontState.themeFonts = fontMeta.value;
    } else if (key === 'installed-font' && fontState.installedFonts == null) {
      fontState.installedFonts = fontMeta.value;
    }
  },
  [TYPES.SET_SHOP_METER](state: State, payload: Meter) {
    return (state.shopMeter = payload);
  },
  [TYPES.ADD_NAVBAR_TOAST](state: State, payload: any) {
    return (state.navBarToast = payload);
  },
  [TYPES.REMOVE_NAVBAR_TOAST](state: State) {
    return (state.navBarToast = null);
  },
  [TYPES.REMOVE_NAVBAR_TOAST](state: State) {
    return (state.navBarToast = null);
  },
  [TYPES.SET_CURRENT_SCREEN](state: State, currentScreen: String) {
    return (state.currentScreen = currentScreen);
  },
  [TYPES.UPDATE_LAST_GEM_METER_USED_AT](state: State, value: string) {
    return (state.shopInfo!.lastGemMeterUsedAt = value);
  },
  [TYPES.DECREASE_NUMBER_OF_PUBLISHED_PRODUCT_PAGE](state: State, value: number) {
    return (state!.shopInfo!.publishedProductPagesCount = state!.shopInfo!.publishedProductPagesCount - value);
  },
  [TYPES.DECREASE_NUMBER_OF_PUBLISHED_ARTICLE_PAGE](state: State, value: number) {
    return (state!.shopInfo!.publishedArticlePagesCount = state!.shopInfo!.publishedArticlePagesCount - value);
  },
  [TYPES.DECREASE_NUMBER_OF_PUBLISHED_PAGE](state: State, value: number) {
    return (state!.shopInfo!.publishedPagesCount = state!.shopInfo!.publishedPagesCount - value);
  },
  [TYPES.COMMIT_PAGE_INFO](state: State, pageInfo: TypePage) {
    return (state.pageInfo = pageInfo);
  },
  // [TYPES.SET_SHOP_INFO_V7](state: State, shopInfoV7: IShopInfoV7) {
  //   return (state.shopInfoV7 = shopInfoV7);
  // },
  [TYPES.SET_SHOP_INFO_V7](state: State, shopInfoV7: IShopInfoV7) {
    const shopInfo = {
      ...state.shopInfoV7,
      ...shopInfoV7,
    };
    return (state.shopInfoV7 = shopInfo);
  },
};

export default mutations;
